import {
    Box,
    Paper,
    Stack,
    ThemeProvider,
    Typography,
    type SxProps,
    type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';

import PageBackground from 'components/v2/PageBackground';

import appThemeV2 from 'theme/themeV2';
import { getDomainBranding, getDomainTagline, openDomainExternalUrl } from 'utils/domainBranding';

/** Shared auth primary button styles — flat hover, modest emphasis */
export const authSubmitButtonSx: SxProps<Theme> = {
    mt: 0.5,
    py: 1.5,
    borderRadius: 1.5,
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
    boxShadow: 'none',
    background: (theme) => theme.palette.primary.main,
    '&:hover': {
        background: (theme) => theme.palette.primary.dark,
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        background: (theme) => theme.palette.primary.main,
        color: '#fff',
        opacity: 0.7,
    },
};

interface AuthLayoutProps {
    /** Main heading under the brand logo */
    title?: ReactNode;
    /** Supporting text under the title */
    subtitle?: ReactNode;
    /** Page body (form, confirmation, etc.) */
    children: ReactNode;
    /** Optional content below the body (links, register CTA, etc.) */
    footer?: ReactNode;
    /** Show domain logo + tagline (default true) */
    showBrand?: boolean;
}

/**
 * Shared shell for public auth screens (login, password recovery, reset password).
 *
 * Mobile: full-viewport solid surface, no card border/shadow — content centered.
 * Desktop: compact card with light radius + soft shadow only.
 */
const AuthLayout = ({ title, subtitle, children, footer, showBrand = true }: AuthLayoutProps) => {
    const branding = getDomainBranding();
    const handleLogoClick = branding.externalUrl ? () => openDomainExternalUrl() : undefined;

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground
                maxWidth={false}
                rootSx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                sx={{
                    mt: 0,
                    mb: 0,
                    // Kill default PageBackground margins so mobile can go edge-to-edge
                    minHeight: '100dvh',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 0, sm: 3 },
                    px: { xs: 0, sm: 2 },
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', sm: 420 },
                        minHeight: { xs: '100dvh', sm: 'auto' },
                        mx: 'auto',
                        // Full-screen on mobile; card padding on larger screens
                        px: { xs: 3, sm: 4 },
                        py: { xs: 4, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        // Mobile: no card chrome — solid surface fills the viewport
                        borderRadius: { xs: 0, sm: 1.5 },
                        border: 'none',
                        bgcolor: {
                            xs: 'background.paper',
                            sm: 'background.paper',
                        },
                        backdropFilter: 'none',
                        // Soft shadow only from tablet up
                        boxShadow: {
                            xs: 'none',
                            sm: '0 0 8px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
                        },
                    }}
                >
                    <Stack
                        spacing={{ xs: 2.5, sm: 3 }}
                        sx={{
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: 360,
                            mx: 'auto',
                        }}
                    >
                        {showBrand && (
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={branding.logo}
                                    alt="Logo"
                                    onClick={handleLogoClick}
                                    sx={{
                                        display: 'block',
                                        width: '100%',
                                        maxWidth: {
                                            xs: `min(${branding.compactWidth}, 56vw)`,
                                            sm: branding.compactWidth,
                                        },
                                        height: 'auto',
                                        objectFit: 'contain',
                                        mb: branding.showTagline ? 1 : 0,
                                        cursor: handleLogoClick ? 'pointer' : 'default',
                                        WebkitTapHighlightColor: 'transparent',
                                    }}
                                />
                                {branding.showTagline && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            textAlign: 'center',
                                            color: 'primary.main',
                                            letterSpacing: '0.02em',
                                        }}
                                    >
                                        {getDomainTagline()}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {(title || subtitle) && (
                            <Box sx={{ width: '100%', textAlign: 'center' }}>
                                {title && (
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.375rem', sm: '1.5rem' },
                                            letterSpacing: '-0.02em',
                                            color: 'text.primary',
                                            mb: subtitle ? 0.5 : 0,
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                )}
                                {subtitle && (
                                    <Typography variant="body2" color="text.secondary">
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Box sx={{ width: '100%' }}>{children}</Box>

                        {footer}
                    </Stack>
                </Paper>
            </PageBackground>
        </ThemeProvider>
    );
};

export default AuthLayout;
